import { css } from 'aphrodite/no-important';
import { useMediaQuery } from 'hooks';
import { CarouselProvider, Dot, DotGroup, Slide, Slider } from 'pure-react-carousel';
import 'pure-react-carousel/dist/react-carousel.es.css';
import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import Truncate from 'react-truncate';
import { isExhibitorUser } from 'redux/reducers/user';
import { GAEventAction, GAEventCategory, GAEventLabel, trackGoogleEvent } from 'util/google-helpers';
import { checkIfTrue } from 'util/helpers';
import { carouselStyle, CAROUSEL_SIZE, dotStyle, slideStyle } from './style';

interface RenderDotsProps {
  currentSlide: number;
  totalSlides: number;
  visibleSlides: number;
  disableActiveDots: boolean;
  showAsSelectedForCurrentSlideOnly: boolean;
}

interface Props {
  className?: string;
  slides: InfoCarouselSlide[];
  displayTime?: number;
}

export interface InfoCarouselSlide {
  title?: string;
  image: { url_mobile: string; url_desktop: string; backgroundPosition: string };
  paragraph?: string;
  linkUrl?: string;
  display: string;
  display_to_exhibitors: string;
}

export const InfoCarousel: FC<Props> = ({ slides, displayTime = 5000 }) => {
  const isExhibitor = useSelector(isExhibitorUser);
  const activePages = slides
    .filter((page) => checkIfTrue(page.display))
    .filter((page) => !isExhibitor || checkIfTrue(page.display_to_exhibitors));
  const dotStyles = dotStyle();
  const carouselStyles = carouselStyle();
  const carouselSize = useMediaQuery(
    ['(min-width: 980px)', '(min-width: 768px)'],
    [CAROUSEL_SIZE.desktop, CAROUSEL_SIZE.tablet],
    CAROUSEL_SIZE.mobile
  );

  const renderSlides = () => {
    return activePages.map((slide, index) => {
      const { url_mobile, url_desktop, backgroundPosition } = activePages[index].image;
      const styles = slideStyle({
        imageUrlMobile: url_mobile,
        imageUrlDesktop: url_desktop,
        imagePosition: backgroundPosition,
      });

      return (
        <Slide key={index} index={index}>
          <div className={css(styles.image, styles.background)}>
            <Link
              to={{ pathname: slide.linkUrl }}
              onClick={() => trackGoogleEvent(GAEventCategory.SITE, GAEventAction.CLICK, GAEventLabel.CAROUSEL_SLIDE)}>
              <div className={css(styles.content)}>
                <h1 className={css(styles.title)}>{slide.title}</h1>
                <div className={css(styles.paragraph)}>
                  <Truncate lines={3}>{slide.paragraph}</Truncate>
                </div>
              </div>
            </Link>
          </div>
        </Slide>
      );
    });
  };

  const renderDots = ({
    currentSlide,
    totalSlides,
    visibleSlides,
    disableActiveDots,
    showAsSelectedForCurrentSlideOnly,
  }: RenderDotsProps) => {
    return [...Array(totalSlides)].map((dot, i) => {
      const multipleSelected = i >= currentSlide && i < currentSlide + visibleSlides;
      const singleSelected = i === currentSlide;
      const selected = showAsSelectedForCurrentSlideOnly ? singleSelected : multipleSelected;
      const slide = i >= totalSlides - visibleSlides ? totalSlides - visibleSlides : i;
      return (
        <Dot
          key={i}
          slide={slide}
          disabled={disableActiveDots ? selected : false}
          className={css(dotStyles.paginationItem, selected && dotStyles.itemSelected)}></Dot>
      );
    });
  };

  return (
    <CarouselProvider
      className={css(carouselStyles.container)}
      interval={displayTime}
      infinite={true}
      isPlaying={true}
      visibleSlides={1}
      naturalSlideWidth={carouselSize.width}
      naturalSlideHeight={carouselSize.height}
      lockOnWindowScroll={true}
      totalSlides={activePages.length}>
      <Slider>{renderSlides()}</Slider>
      <DotGroup className={css(dotStyles.pagination)} renderDots={renderDots as any} />
    </CarouselProvider>
  );
};
